import { useEffect } from 'react';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  ogType?: string;
  ogLocale?: string;
  ogSiteName?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  twitterSite?: string;
  author?: string;
  robots?: string;
  geoRegion?: string;
  geoPlacename?: string;
  geoPosition?: string;
  icbm?: string;
  structuredData?: object | object[];
  lastModified?: string;
  breadcrumbs?: Array<{ name: string; url: string }>;
}

const SEOHead = ({
  title = 'Technelle.ai | AI-Powered Marketing & Automation in Waukesha, WI',
  description = 'Transform your Wisconsin business with AI-powered marketing strategies and workflow automation. Based in Waukesha, serving all of Wisconsin. 20+ years of expertise helping SMBs achieve measurable revenue growth.',
  keywords = 'AI marketing consultancy, Waukesha marketing, Wisconsin marketing agency, workflow automation Wisconsin, AI implementation, marketing strategy, demand generation, lead funnels, business automation, SMB consulting, AI consulting',
  canonical = 'https://technelle.ai/',
  ogTitle,
  ogDescription,
  ogImage = 'https://static.readdy.ai/image/cb419a93222bfd6610832c3481bbaa16/2c1578fc61a5e3d5ea92991827e265f3.png',
  ogUrl,
  ogType = 'website',
  ogLocale = 'en_US',
  ogSiteName = 'Technelle',
  twitterCard = 'summary_large_image',
  twitterTitle,
  twitterDescription,
  twitterImage,
  twitterSite = '@technelleai',
  author = 'Technelle.ai',
  robots = 'index, follow',
  geoRegion = 'US-WI',
  geoPlacename = 'Waukesha',
  geoPosition = '43.0117;-88.2315',
  icbm = '43.0117, -88.2315',
  structuredData,
  lastModified = new Date().toISOString().split('T')[0],
  breadcrumbs,
}: SEOHeadProps) => {
  const finalOgTitle = ogTitle || title;
  const finalOgDescription = ogDescription || description;
  const finalOgUrl = ogUrl || canonical;
  const finalTwitterTitle = twitterTitle || title;
  const finalTwitterDescription = twitterDescription || description;
  const finalTwitterImage = twitterImage || ogImage;

  useEffect(() => {
    // Set document title
    document.title = title;

    // Helper function to set or update meta tags
    const setMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Helper function to set or update link tags
    const setLinkTag = (rel: string, href: string) => {
      let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      
      element.href = href;
    };

    // Primary Meta Tags
    setMetaTag('title', title);
    setMetaTag('description', description);
    setMetaTag('keywords', keywords);
    setMetaTag('author', author);
    setMetaTag('robots', robots);
    setMetaTag('last-modified', lastModified);
    setLinkTag('canonical', canonical);

    // Geo Tags for Local SEO
    setMetaTag('geo.region', geoRegion);
    setMetaTag('geo.placename', geoPlacename);
    setMetaTag('geo.position', geoPosition);
    setMetaTag('ICBM', icbm);

    // Open Graph / Facebook
    setMetaTag('og:type', ogType, true);
    setMetaTag('og:url', finalOgUrl, true);
    setMetaTag('og:title', finalOgTitle, true);
    setMetaTag('og:description', finalOgDescription, true);
    setMetaTag('og:image', ogImage, true);
    setMetaTag('og:locale', ogLocale, true);
    setMetaTag('og:site_name', ogSiteName, true);

    // Twitter
    setMetaTag('twitter:card', twitterCard, true);
    setMetaTag('twitter:site', twitterSite, true);
    setMetaTag('twitter:url', finalOgUrl, true);
    setMetaTag('twitter:title', finalTwitterTitle, true);
    setMetaTag('twitter:description', finalTwitterDescription, true);
    setMetaTag('twitter:image', finalTwitterImage, true);

    // Build comprehensive structured data array
    const allStructuredData: object[] = [];

    // Add WebSite structured data (always include)
    const siteUrl = (import.meta.env.VITE_SITE_URL as string) || 'https://technelle.ai';
    const websiteData = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Technelle",
      "url": siteUrl,
      "description": "AI-powered marketing consultancy specializing in workflow automation, demand generation, and strategic marketing for Wisconsin businesses.",
      "potentialAction": {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": `${siteUrl}/?s={search_term_string}`
        },
        "query-input": "required name=search_term_string"
      }
    };
    allStructuredData.push(websiteData);

    // Add WebPage structured data
    const webPageData = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": title,
      "description": description,
      "url": canonical,
      "isPartOf": {
        "@type": "WebSite",
        "url": siteUrl
      },
      "dateModified": lastModified,
      "inLanguage": "en-US",
      "publisher": {
        "@type": "Organization",
        "name": "Technelle",
        "url": siteUrl
      }
    };
    allStructuredData.push(webPageData);

    // Add Organization structured data (always include)
    const organizationData = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Technelle.ai",
      "alternateName": "Technelle",
      "url": "https://technelle.ai",
      "logo": "https://static.readdy.ai/image/cb419a93222bfd6610832c3481bbaa16/2c1578fc61a5e3d5ea92991827e265f3.png",
      "description": "AI-powered marketing consultancy specializing in workflow automation, demand generation, and strategic marketing for small and mid-size businesses.",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Waukesha",
        "addressRegion": "WI",
        "addressCountry": "US"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+1-414-530-9114",
        "contactType": "Customer Service",
        "email": "melissa@technelle.ai",
        "areaServed": ["US-WI", "US"],
        "availableLanguage": "English"
      },
      "founder": {
        "@type": "Person",
        "name": "Melissa Thomas",
        "jobTitle": "Founder & Chief Strategist"
      },
      "sameAs": [
        "https://www.linkedin.com/company/technelle"
      ]
    };
    allStructuredData.push(organizationData);

    // Add LocalBusiness structured data (always include for local SEO)
    const localBusinessData = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Technelle.ai",
      "image": "https://static.readdy.ai/image/cb419a93222bfd6610832c3481bbaa16/2c1578fc61a5e3d5ea92991827e265f3.png",
      "url": "https://technelle.ai",
      "telephone": "+1-414-530-9114",
      "email": "melissa@technelle.ai",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Waukesha",
        "addressRegion": "WI",
        "addressCountry": "US"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "43.0117",
        "longitude": "-88.2315"
      },
      "priceRange": "$$",
      "areaServed": [
        {
          "@type": "City",
          "name": "Waukesha"
        },
        {
          "@type": "City",
          "name": "Milwaukee"
        },
        {
          "@type": "City",
          "name": "Madison"
        },
        {
          "@type": "State",
          "name": "Wisconsin"
        }
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Marketing Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "AI Marketing Implementation",
              "description": "Practical AI solutions for marketing automation and workflow optimization"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Demand Generation",
              "description": "Strategic campaigns to generate qualified leads and drive revenue growth"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Marketing Strategy",
              "description": "Comprehensive marketing planning and execution for SMBs"
            }
          }
        ]
      }
    };
    allStructuredData.push(localBusinessData);

    // Add BreadcrumbList if breadcrumbs provided
    if (breadcrumbs && breadcrumbs.length > 0) {
      const breadcrumbData = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((crumb, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "name": crumb.name,
          "item": crumb.url
        }))
      };
      allStructuredData.push(breadcrumbData);
    }

    // Add custom structured data if provided
    if (structuredData) {
      if (Array.isArray(structuredData)) {
        allStructuredData.push(...structuredData);
      } else {
        allStructuredData.push(structuredData);
      }
    }

    // Remove existing structured data scripts
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach(script => script.remove());

    // Add all structured data as separate script tags (recommended by Google)
    allStructuredData.forEach((data) => {
      const scriptElement = document.createElement('script');
      scriptElement.setAttribute('type', 'application/ld+json');
      scriptElement.textContent = JSON.stringify(data);
      document.head.appendChild(scriptElement);
    });
  }, [
    title,
    description,
    keywords,
    canonical,
    ogTitle,
    ogDescription,
    ogImage,
    ogUrl,
    ogType,
    ogLocale,
    ogSiteName,
    twitterCard,
    twitterTitle,
    twitterDescription,
    twitterImage,
    twitterSite,
    author,
    robots,
    geoRegion,
    geoPlacename,
    geoPosition,
    icbm,
    structuredData,
    lastModified,
    breadcrumbs,
    finalOgTitle,
    finalOgDescription,
    finalOgUrl,
    finalTwitterTitle,
    finalTwitterDescription,
    finalTwitterImage,
  ]);

  return null;
};

export default SEOHead;
